import {
    BLOCK_PARAGRAPH,
    BLOCK_TABS,
    isBlockEmpty,
    moveToNextLeafBlock,
    tabs,
} from '@gitbook/doc-core';
import {
    Block,
    type Change,
    isBlockRangeSelection,
    Text,
    TextRange,
    type Value,
} from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

import { isBackspace, isEnter, isModA, isModEnter, selectAllInContainerFirst } from '../common';

export const plugin: Plugin = {
    onKeyDown,
};

/**
 * Handle key down events for tabs.
 */
function onKeyDown(event: KeyboardEvent, change: Change): Change | void {
    const { value } = change;

    if (isBlockRangeSelection(value.selection)) {
        return;
    }

    // Pressing backspace in an empty tab item keeps focus in that tab.
    if (isBackspace(event) && value.isCollapsed) {
        return onBackspace(event, change);
    }

    // Exit the tab with "double" enter.
    if (isEnter(event)) {
        return onEnter(event, change);
    }

    // Exit the tab with alternate enter.
    if (isModEnter(event)) {
        return onModEnter(event, change);
    }

    if (isModA(event)) {
        event.preventDefault();
        return selectAllInContainerFirst(change, getTabRange);
    }

    return;
}

function onBackspace(event: Event, change: Change): Change | void {
    const { value } = change;

    const currentTab = tabs.getCurrentTab(value);
    if (!currentTab) {
        return;
    }

    // If tab isn't empty, do nothing.
    const isTabEmpty = isBlockEmpty(currentTab);
    if (!isTabEmpty) {
        return;
    }

    event.preventDefault();

    // If tab is empty, keep focus in the current tab.
    return change.focus().collapseToStartOf(currentTab);
}

function onEnter(event: Event, change: Change): Change | void {
    const { value } = change;

    const currentTab = tabs.getCurrentTab(value);
    if (!currentTab) {
        return;
    }

    if (currentTab.nodes.size <= 1 || !isBlockEmpty(currentTab.blockNodes.last()!)) {
        return;
    }

    const blockTabs = value.document.getClosest(
        value.startKey!,
        (parent) => 'type' in parent && parent.type === BLOCK_TABS
    );
    if (!blockTabs) {
        throw new Error(
            'Could not find parent of type BLOCK_TABS while exiting tab when enter is pressed twice'
        );
    }

    const parent = value.document.getParent(blockTabs.key);
    if (!parent) {
        throw new Error(
            'Could not find parent of tab while exiting tab when enter is pressed twice'
        );
    }

    const index = parent.getChildIndex(blockTabs.key);
    if (index < 0) {
        throw new Error(
            'Could not find child of parent while exiting tab when enter is pressed twice'
        );
    }

    event.preventDefault();

    // Remove the last empty paragraph
    const lastBlock = currentTab.blockNodes.last() as Block | undefined;
    if (lastBlock) {
        change.removeNodeByKey(lastBlock.key);
    }

    // Create new empty paragraph under the tabs block and focus it
    const paragraph = Block.create({
        type: BLOCK_PARAGRAPH,
        nodes: [Text.create('')],
    });

    return change.insertNodeByKey(parent.key, index + 1, paragraph).collapseToStartOf(paragraph);
}

function onModEnter(event: Event, change: Change): Change | void {
    const { value } = change;

    const currentTab = tabs.getCurrentTab(value);
    if (!currentTab) {
        return;
    }

    const blockTabs = value.document.getClosest(
        value.startKey!,
        (parent) => 'type' in parent && parent.type === BLOCK_TABS
    );
    if (!blockTabs) {
        throw new Error('Could not find parent of type BLOCK_TABS while exiting tab on mod+enter');
    }

    const parent = value.document.getParent(blockTabs.key);
    if (!parent) {
        throw new Error('Could not find parent of tab while exiting tab on mod+enter');
    }

    const index = parent.getChildIndex(blockTabs.key);
    if (index < 0) {
        throw new Error('Could not find child of parent while exiting tab on mod+enter');
    }

    event.preventDefault();

    moveToNextLeafBlock(change, blockTabs as Block);

    return change;
}

function getTabRange(value: Value): TextRange | null {
    const currentTab = tabs.getCurrentTab(value);
    if (!currentTab) {
        return null;
    }
    const firstText = currentTab.getFirstText();
    const lastText = currentTab.getLastText();
    if (!firstText || !lastText) {
        return null;
    }

    return TextRange.create().moveToRangeOf(firstText, lastText);
}
